import  { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";
import Logo from "./Logo";
import { getNavigation } from "../api/navigation";

const AI_LIVE_URL = "/ai-intelligence";
const AI_LIVE_LABEL = "inLoop AI Live";

// The drawer takes over at 1200 (see Header.css); hover is also unreliable on
// touch devices at any width, so dropdowns fall back to tap-toggle there.
const DRAWER_BREAKPOINT = 1200;
const prefersTapDropdown = () =>
  window.innerWidth <= DRAWER_BREAKPOINT ||
  window.matchMedia("(hover: none)").matches;

const VIEW_ALL_LABELS = {
  "/product-features": "View all features",
};

const AiLiveIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="nav-ai-live__icon"
  >
    <path
      d="M22.539 15.6421C21.267 15.6421 20.2108 16.5938 20.0631 17.8288C20.0631 17.8628 20.0631 17.9081 20.0631 17.9421C19.7111 21.2504 16.7581 23.7883 13.2828 23.4711C10.3753 23.2105 8.00163 20.8879 7.68362 17.9988C7.29747 14.5658 9.7393 11.6087 12.9761 11.1442C13.4191 11.0762 13.8734 11.0762 14.3163 11.1102L14.782 11.1442C14.8842 11.1555 14.9978 11.1668 15.1 11.1668C16.4742 11.1668 17.5872 10.0565 17.5872 8.68556C17.5872 7.49592 16.7468 6.49889 15.6111 6.26096C15.4975 6.2383 15.3839 6.22697 15.2703 6.21564C14.816 6.15899 14.339 6.125 13.8734 6.125C7.53598 6.125 2.43653 11.3708 2.67503 17.7382C2.90218 23.5617 7.67226 28.2863 13.5099 28.4676C19.3476 28.6489 24.4357 24.1849 25.0149 18.418C25.0149 18.384 25.0149 18.3387 25.0149 18.2933C25.0149 18.2367 25.0149 18.18 25.0149 18.1234C25.0149 16.7525 23.9019 15.6421 22.5277 15.6421H22.539Z"
      fill="currentColor"
    />
    <path
      d="M19.5989 9.66333C22.4019 10.4376 22.8967 10.9325 23.67 13.7344C23.7418 13.9936 23.9785 14.1731 24.2467 14.1731C24.5149 14.1731 24.7528 13.9936 24.8235 13.7344C25.5966 10.9325 26.0915 10.4375 28.8946 9.66333C29.1538 9.59152 29.3333 9.35588 29.3333 9.08657C29.3333 8.81726 29.1538 8.58162 28.8946 8.5098C26.0915 7.73555 25.5968 7.24068 24.8235 4.43873C24.7528 4.17953 24.516 4 24.2467 4C23.9774 4 23.7418 4.17954 23.67 4.43873C22.8957 7.24178 22.4008 7.73564 19.5989 8.5098C19.3397 8.58162 19.1602 8.81726 19.1602 9.08657C19.1602 9.35588 19.3397 9.59152 19.5989 9.66333Z"
      fill="currentColor"
    />
  </svg>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [navError, setNavError] = useState(null);

  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const loadNavigation = async () => {
      try {
        const res = await getNavigation(); 
        setNavigation(res.data); 
      } catch (err) {
        console.error("Failed to load navigation from Strapi:", err);
        setNavError(err.message);
      }
    };

    loadNavigation();
  }, []);

  useEffect(() => {
    const checkScrollPosition = () => {
      setIsScrolled(window.scrollY > 50);
    };

    checkScrollPosition();
    const timeoutId = setTimeout(checkScrollPosition, 100);

    const handleScroll = () => checkScrollPosition();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  const shouldBeScrolled = !isHomePage || isScrolled;

  const isActive = (path) => {
    if (!path) return false;
    const clean = path.split("#")[0];
    return location.pathname === clean || location.pathname.startsWith(clean);
  };

  const handleNavLinkClick = () => {
    if (window.innerWidth <= DRAWER_BREAKPOINT) {
      setIsMenuOpen(false);
      setOpenDropdownId(null);
    }
  };

  const strapiBaseUrl = process.env.REACT_APP_STRAPI_URL || "";

  const logoDefaultSrc = navigation?.logoDefault?.url
    ? `${strapiBaseUrl}${navigation.logoDefault.url}`
    : undefined;
  const logoInvertedSrc = navigation?.logoInverted?.url
    ? `${strapiBaseUrl}${navigation.logoInverted.url}`
    : undefined;

  const navItems = navigation?.items || [];
  const ctaButton = navigation?.ctaButton;
  const ctaUrl = ctaButton?.url || "/contact-us";
  const ctaLabel = ctaButton?.label || "Contact Us";

  const getItemUrls = (item) => {
    const urls = [];

    if (item.url && !item.isExternal) {
      urls.push(item.url);
    }

    (item.childrenGroups || []).forEach((group) => {
      if (group.url) urls.push(group.url);
      (group.items || []).forEach((link) => {
        if (link.url && !link.isExternal) {
          urls.push(link.url);
        }
      });
    });

    return urls;
  };

  const isDropdownActive = (item) => {
    const urls = getItemUrls(item);
    return urls.some(
      (u) => location.pathname === u || location.pathname.startsWith(u)
    );
  };

  const renderDropdownItem = (item) => {
    const isOpen = openDropdownId === item.id;
    const groups = item.childrenGroups || [];
    const isMegaMenu = groups.length > 1;
    const triggerUrl = item.url && !item.isExternal ? item.url : null;
    const viewAllLabel = triggerUrl
      ? VIEW_ALL_LABELS[triggerUrl] || "View all"
      : null;

    const handleTriggerClick = (event) => {
      if (prefersTapDropdown()) {
        event.preventDefault();
        setOpenDropdownId(isOpen ? null : item.id);
        return;
      }
      if (triggerUrl) handleNavLinkClick();
    };

    const triggerContent = (
      <>
        {item.label}
        <svg
          width="20"
          height="20"
          viewBox="0 0 21 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="dropdown-arrow"
        >
          <path
            d="M10.2186 13.9841C10.5584 14.2448 11.0426 14.2262 11.3619 13.9284L17.1424 8.53321C17.5002 8.19927 17.5199 7.63863 17.1861 7.28075C16.8522 6.92297 16.2915 6.90332 15.9336 7.23709L10.7575 12.0685L5.58133 7.23709C5.22345 6.90332 4.6628 6.92297 4.32887 7.28075C3.9951 7.63863 4.01474 8.19927 4.37253 8.53321L10.1531 13.9284L10.2186 13.9841Z"
            fill="currentColor"
          />
        </svg>
      </>
    );

    const triggerClassName = `nav-link ${
      isDropdownActive(item) ? "active" : ""
    }`;

    return (
      <div
        key={item.id}
        className={`nav-item dropdown ${isMegaMenu ? "dropdown-mega" : ""}`}
        onMouseEnter={() => {
          if (!prefersTapDropdown()) setOpenDropdownId(item.id);
        }}
        onMouseLeave={() => {
          if (!prefersTapDropdown()) setOpenDropdownId(null);
        }}
      >
        {triggerUrl ? (
          <Link
            to={triggerUrl}
            className={triggerClassName}
            aria-expanded={isOpen}
            onClick={handleTriggerClick}
          >
            {triggerContent}
          </Link>
        ) : (
          <span
            className={triggerClassName}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            onClick={handleTriggerClick}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpenDropdownId(isOpen ? null : item.id);
              }
            }}
          >
            {triggerContent}
          </span>
        )}

        {isOpen && (
          <div
            className={`dropdown-content ${isMegaMenu ? "dropdown-large" : ""}`}
          >
            {groups.map((group) => (
              <div className="dropdown-column" key={group.id}>
                {(group.title || group.label) && (
                  <Link
                    to={group.url || "#"}
                    className="dropdown-title"
                    onClick={handleNavLinkClick}
                  >
                    {group.title || group.label}
                  </Link>
                )}

                <div className="dropdown-items">
                  {(group.items || []).map((link) => {
                    let href = "#";

                    if (link.url) {
                      if (link.url.startsWith("#") && group.url) {
                        href = `${group.url}${link.url}`;
                      } else {
                        href = link.url;
                      }
                    } else if (group.url) {
                      href = group.url;
                    }

                    const iconUrl = link.iconImage?.url
                      ? `${strapiBaseUrl}${link.iconImage.url}`
                      : null;

                    return (
                      <Link
                        key={link.id}
                        to={href}
                        className="dropdown-item"
                        onClick={handleNavLinkClick}
                      >
                        {iconUrl && (
                          <img
                            src={iconUrl}
                            alt={link.label}
                            className="dropdown-icon"
                          />
                        )}
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {viewAllLabel && (
              <Link
                to={triggerUrl}
                className="dropdown-item dropdown-view-all nav-mobile-only"
                onClick={handleNavLinkClick}
              >
                {viewAllLabel}
              </Link>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLinkItem = (item) => {
    const href = item.url || "/";

    return (
      <Link
        key={item.id}
        to={href}
        className={`nav-link ${isActive(href) ? "active" : ""}`}
        onClick={handleNavLinkClick}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header className={`header ${shouldBeScrolled ? "scrolled" : ""}`}>
      <div className="container">
        <div className="header-content">
          <Logo
            isScrolled={shouldBeScrolled}
            isHomePage={isHomePage}
            defaultLogoSrc={logoDefaultSrc}
            invertedLogoSrc={logoInvertedSrc}
          />

          <nav className={`nav ${isMenuOpen ? "nav-open" : ""}`}>
            <div className="nav-group">
              {navItems.map((item) =>
                item.type === "dropdown"
                  ? renderDropdownItem(item)
                  : renderLinkItem(item)
              )}
              <Link
                to={AI_LIVE_URL}
                className="nav-link nav-mobile-only nav-ai-live-mobile"
                onClick={handleNavLinkClick}
              >
                <AiLiveIcon />
                {AI_LIVE_LABEL}
              </Link>
              <Link
                to={ctaUrl}
                className={`nav-link nav-mobile-only ${
                  isActive(ctaUrl) ? "active" : ""
                }`}
                onClick={handleNavLinkClick}
              >
                {ctaLabel}
              </Link>
            </div>
          </nav>

          <div className="nav-actions">
            <Link
              to={AI_LIVE_URL}
              className="nav-ai-live"
              onClick={handleNavLinkClick}
            >
              <AiLiveIcon />
              <span>{AI_LIVE_LABEL}</span>
            </Link>
            <Link
              to={ctaUrl}
              className="btn btn-secondary"
              onClick={handleNavLinkClick}
            >
              {ctaLabel}
            </Link>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              setOpenDropdownId(null);
            }}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {navError && (
        <div className="nav-error-banner">
          Navigation could not be loaded: {navError}
        </div>
      )}
    </header>
  );
};

export default Header;
